import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../../models';
import { ErrorMessagesViewModelKey, IErrorMessageComponentProps } from '../../../../../../models/AppState';
import { IFormField } from '../../../../../../viewmodels/AppViewModels';
import { Button } from '../../../../../components-v2/Button';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { asModalComponent } from '../../../../../components/Modal';
import { baseStyleSheet } from '../../../../../styles/styles';

import { ReorderQuestionItem } from './ReorderQuestionItem';
import { styleSheet } from './styles';

interface IReorderQuestionsResult {
	reorderedQuestions?: IFormField<string>[];
	canceled?: boolean;
}

interface IProps
	extends IEventLoggingComponentProps,
		IModalContext<IReorderQuestionsResult>,
		IErrorMessageComponentProps {
	questions: IFormField<string>[];
	surveyId: string;
	impersonationContext?: any;
	className?: string;
	styles?: StyleDeclarationValue[];
}

const _ReorderQuestionsModal: React.FC<IProps> = props => {
	const { questions: propsQuestions, surveyId, parentModal } = props;

	const validQuestions = propsQuestions.filter(
		question => question.id && question.label && question.label.trim() !== '' && question._type
	);
	const unsavedQuestions = propsQuestions.filter(
		question => question.id && question.label && question.label.trim() !== '' && !question.name
	);

	const [questions, setQuestions] = useState<IFormField<string>[]>(validQuestions);
	const [_saving, _setSaving] = useState(false);

	useEffect(() => {
		const newValidQuestions = propsQuestions.filter(
			question => question.id && question.label && question.label.trim() !== '' && question._type
		);
		setQuestions(newValidQuestions);
	}, [propsQuestions]);

	const onDragEnd = useCallback(
		(result: DropResult) => {
			if (result.destination && result.source.index !== result.destination.index) {
				const newQuestions = [...questions];
				const [removed] = newQuestions.splice(result.source.index, 1);
				newQuestions.splice(result.destination.index, 0, removed);
				setQuestions(newQuestions);
			}
		},
		[questions]
	);

	const onCancel = useCallback(() => {
		if (parentModal) {
			parentModal.onRequestClose(undefined, true);
		}
	}, [parentModal]);

	const onSaveClicked = useCallback(async () => {
		if (parentModal) {
			parentModal.onRequestClose({ reorderedQuestions: questions }, false);
		}
	}, [parentModal, questions]);

	const droppableId = `reorder-questions-${surveyId}`;

	if (validQuestions.length === 0) {
		const hasUnsavedQuestions = unsavedQuestions.length > 0;
		return (
			<div className={css(styleSheet.boardHeader)}>
				<div className={css(styleSheet.boardHeaderTitles)}>
					<div className={css(styleSheet.boardHeaderTitle)}>
						{hasUnsavedQuestions ? 'No Saved Questions to Reorder' : 'No Questions Available'}
					</div>
					<div className={css(styleSheet.boardHeaderSubheader)}>
						{hasUnsavedQuestions
							? `You have ${unsavedQuestions.length} unsaved question${unsavedQuestions.length === 1 ? '' : 's'}. Save your form first to enable reordering.`
							: 'There are no questions available to reorder.'}
					</div>
				</div>
				<div className={css(styleSheet.boardFooter)}>
					<Button variant='secondary' onClick={onCancel}>
						Close
					</Button>
				</div>
			</div>
		);
	}

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<div className={css(styleSheet.boardHeader)}>
				<div className={css(styleSheet.boardHeaderTitles)}>
					<div className={css(styleSheet.boardHeaderTitle)}>Reorder Questions</div>
					<div className={css(styleSheet.boardHeaderSubheader)}>
						Drag and drop questions to reorder them.
						{unsavedQuestions.length > 0 && (
							<div className={css(styleSheet.unsavedNotice)}>
								Note: {unsavedQuestions.length} unsaved question{unsavedQuestions.length === 1 ? '' : 's'} cannot be
								reordered. Save your form first to reorder all questions.
							</div>
						)}
					</div>
				</div>
			</div>
			<div className={css(styleSheet.body)}>
				<Droppable direction='vertical' droppableId={droppableId}>
					{(droppableProvided: DroppableProvided) => (
						<div
							className={css(styleSheet.columns)}
							ref={droppableProvided.innerRef}
							{...droppableProvided.droppableProps}
						>
							{questions.map((question, index) => (
								<ReorderQuestionItem key={question.id} question={question} index={index} />
							))}
							{droppableProvided.placeholder}
						</div>
					)}
				</Droppable>
			</div>
			<div className={css(styleSheet.boardFooter)}>
				<Button variant='primary' onClick={onSaveClicked}>
					Save Order
				</Button>
				<Button variant='secondary' onClick={onCancel}>
					Cancel
				</Button>
			</div>
			{_saving && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</DragDropContext>
	);
};

const ReorderQuestionsModalAsObserver = observer(_ReorderQuestionsModal);
const ReorderQuestionsModalWithContext = inject(
	ErrorMessagesViewModelKey,
	ModalChildComponentContextKey
)(ReorderQuestionsModalAsObserver);
const ReorderQuestionsModal = withEventLogging(ReorderQuestionsModalWithContext, 'ReorderQuestionsModal');

export const ReorderQuestionsModalComponent = asModalComponent(ReorderQuestionsModal, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
